/**
 * ADMIN Unified Requests Page
 * รวมคำขอทั้ง 3 ประเภทในหน้าเดียว: CREATE, CLAIM, UPDATE
 * ใช้ tabs เพื่อแยกประเภทคำขอ และแสดง badge จำนวน pending
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiFileText,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiEye,
  FiCheck,
  FiX
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { AdminLayout } from '../../components/admin';
import {
  ClaimCard,
  ApproveClaimModal,
  RejectClaimModal,
  type Claim
} from '../../components/admin/claims';
import ViewClaimDetailsModal from '../../components/admin/claims/ViewClaimDetailsModal';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { apiClient } from '../../services/api';
import { useDialog, prompt } from '../../hooks/useDialog';
import { useServiceTypes } from '../../hooks/useServiceTypes';
import { API_URL } from '../../services/api';

// Types
type TabType = 'CREATE' | 'CLAIM' | 'UPDATE';
type ClaimStatus = 'pending' | 'approved' | 'rejected';

interface ClaimsResponse {
  claims: Claim[];
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

interface ChangeRequest {
  id: string;
  userId: string;
  type: 'CREATE' | 'UPDATE' | 'COMPANY_IMAGE_ADD' | 'COMPANY_IMAGE_DELETE' | 'COMPANY_IMAGE_UPDATE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  requestedData: any;
  currentData?: any;
  changedFields?: Record<string, { old: any; new: any }>;
  userNotes?: string;
  adminNotes?: string;
  createdAt: string;
  reviewedAt?: string;
  companyId?: string;
  user?: {
    email: string;
    firstName: string;
    lastName: string;
  };
  company?: {
    nameTh: string;
  };
}

const UnifiedRequestsPage: React.FC = () => {
  const { token } = useAdminAuth();
  const [errorState, { handleError, clearError }] = useErrorHandler();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();
  const { serviceTypes } = useServiceTypes(false); // Fetch all service types (including inactive)

  // Tab State
  const [activeTab, setActiveTab] = useState<TabType>('CREATE');

  // === CLAIM Requests State ===
  const [claims, setClaims] = useState<Claim[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [claimsSearchQuery, setClaimsSearchQuery] = useState('');
  const [claimStatusFilter, setClaimStatusFilter] = useState<ClaimStatus>('pending');
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [showClaimDetailsModal, setShowClaimDetailsModal] = useState(false);
  const [claimModalLoading, setClaimModalLoading] = useState(false);

  // === CREATE/UPDATE Requests State ===
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [changeRequestsLoading, setChangeRequestsLoading] = useState(true);
  const [changeStatusFilter, setChangeStatusFilter] = useState<string>('PENDING');
  const [selectedChangeRequest, setSelectedChangeRequest] = useState<ChangeRequest | null>(null);
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [changeActionLoading, setChangeActionLoading] = useState(false);

  // Counts
  const [createCount, setCreateCount] = useState(0);
  const [claimCount, setClaimCount] = useState(0);
  const [updateCount, setUpdateCount] = useState(0);

  // Custom API call function
  const callApi = useCallback(
    async <T = any>(options: {
      method: 'GET' | 'POST' | 'PUT' | 'DELETE';
      endpoint: string;
      data?: any;
    }): Promise<{ success: boolean; data: T; message?: string }> => {
      if (!token) {
        throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
      }

      
      const response = await fetch(`${API_URL}${options.endpoint}`, {
        method: options.method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: options.data ? JSON.stringify(options.data) : undefined
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.detail || `HTTP ${response.status}`);
      }

      return {
        success: true,
        data: result.data || result,
        message: result.message
      };
    },
    [token]
  );

  // === Load Claims (CLAIM tab) ===
  const loadClaims = useCallback(
    async (status: ClaimStatus = 'pending') => {
      try {
        setClaimsLoading(true);
        clearError();

        const params = new URLSearchParams({
          page: '1',
          per_page: '100',
          status: status
        });

        const response = await callApi<ClaimsResponse>({
          method: 'GET',
          endpoint: `/api/admin/claims?${params.toString()}`
        });

        if (response.success) {
          setClaims(response.data.claims || []);
        }
      } catch (error) {
        handleError(error, 'ไม่สามารถโหลดรายการคำขอเป็นเจ้าของได้');
        setClaims([]);
      } finally {
        setClaimsLoading(false);
      }
    },
    [token, callApi, handleError, clearError]
  );

  // === Load Change Requests (CREATE/UPDATE tabs) ===
  const loadChangeRequests = useCallback(
    async (type: 'CREATE' | 'UPDATE', status: string = 'PENDING') => {
      try {
        setChangeRequestsLoading(true);
        clearError();

        // For UPDATE tab, load multiple request types including COMPANY_IMAGE_*
        const requestTypes = type === 'UPDATE'
          ? ['UPDATE', 'COMPANY_IMAGE_ADD', 'COMPANY_IMAGE_DELETE', 'COMPANY_IMAGE_UPDATE']
          : [type];

        // Load all request types in parallel
        const promises = requestTypes.map(requestType => {
          const params: any = { limit: 100 };
          if (status !== 'ALL') {
            params.status = status;
          }
          params.type = requestType;
          return apiClient.get('/api/admin/change-requests/', { params });
        });

        const responses = await Promise.all(promises);

        // Combine all results
        const allRequests = responses.flatMap(response => response.data.items || []);

        // Sort by createdAt descending (newest first)
        allRequests.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        setChangeRequests(allRequests);
      } catch (error) {
        handleError(error, 'ไม่สามารถโหลดรายการคำขอเปลี่ยนแปลงได้');
        setChangeRequests([]);
      } finally {
        setChangeRequestsLoading(false);
      }
    },
    [handleError, clearError]
  );

  // === Load Counts (for badges) ===
  const loadCounts = useCallback(async () => {
    try {
      // Load CLAIM count
      const claimParams = new URLSearchParams({ page: '1', per_page: '1', status: 'pending' });
      const claimResponse = await callApi<ClaimsResponse>({
        method: 'GET',
        endpoint: `/api/admin/claims?${claimParams.toString()}`
      });
      setClaimCount(claimResponse.data.pending || 0);

      // Load CREATE count
      const createResponse = await apiClient.get('/api/admin/change-requests/', {
        params: { limit: 1, status: 'PENDING', type: 'CREATE' }
      });
      setCreateCount(createResponse.data.total || createResponse.data.items?.length || 0);

      // Load UPDATE count (including COMPANY_IMAGE_* types)
      const updateTypes = ['UPDATE', 'COMPANY_IMAGE_ADD', 'COMPANY_IMAGE_DELETE', 'COMPANY_IMAGE_UPDATE'];
      const updatePromises = updateTypes.map(type =>
        apiClient.get('/api/admin/change-requests/', {
          params: { limit: 100, status: 'PENDING', type }
        })
      );
      const updateResponses = await Promise.all(updatePromises);
      const totalUpdateCount = updateResponses.reduce((sum, response) => {
        return sum + (response.data.total || response.data.items?.length || 0);
      }, 0);
      setUpdateCount(totalUpdateCount);
    } catch (error) {
      console.error('Failed to load counts:', error);
    }
  }, [token, callApi]);

  // Initial load
  useEffect(() => {
    if (token) {
      loadCounts();

      // Load data based on active tab
      if (activeTab === 'CLAIM') {
        loadClaims('pending');
      } else if (activeTab === 'CREATE') {
        loadChangeRequests('CREATE', 'PENDING');
      } else if (activeTab === 'UPDATE') {
        loadChangeRequests('UPDATE', 'PENDING');
      }
    }
  }, [activeTab, token, loadCounts, loadClaims, loadChangeRequests]);

  // === CLAIM Handlers ===
  const handleApproveClaimConfirm = async (claimId: string, notes?: string) => {
    setClaimModalLoading(true);
    try {
      const requestBody: any = {};
      if (notes) {
        requestBody.notes = notes;
      }

      const response = await callApi({
        method: 'PUT',
        endpoint: `/api/admin/claims/${claimId}/approve`,
        data: requestBody
      });

      if (response.success) {
        toast.success('อนุมัติคำขอเรียบร้อยแล้ว');
        await loadClaims(claimStatusFilter);
        await loadCounts();
      }
    } catch (error) {
      throw error;
    } finally {
      setClaimModalLoading(false);
    }
  };

  const handleRejectClaimConfirm = async (claimId: string, reason: string) => {
    setClaimModalLoading(true);
    try {
      const response = await callApi({
        method: 'PUT',
        endpoint: `/api/admin/claims/${claimId}/reject`,
        data: { reason: reason }
      });

      if (response.success) {
        toast.success('ปฏิเสธคำขอเรียบร้อยแล้ว');
        await loadClaims(claimStatusFilter);
        await loadCounts();
      }
    } catch (error) {
      throw error;
    } finally {
      setClaimModalLoading(false);
    }
  };

  const handleViewClaimDetails = (claim: Claim) => {
    setSelectedClaim(claim);
    setShowClaimDetailsModal(true);
  };

  const handleApproveClaimClick = (claimId: string, companyName: string) => {
    const claim = claims.find((c) => c.id === claimId);
    if (claim) {
      setSelectedClaim(claim);
      setApproveModalOpen(true);
    }
  };

  const handleRejectClaimClick = (claimId: string, companyName: string) => {
    const claim = claims.find((c) => c.id === claimId);
    if (claim) {
      setSelectedClaim(claim);
      setRejectModalOpen(true);
    }
  };

  // === CHANGE REQUEST Handlers ===
  const handleViewChangeRequestDetail = (request: ChangeRequest) => {
    setSelectedChangeRequest(request);
    setShowChangeRequestModal(true);
  };

  const handleApproveChangeRequest = async (requestId: string, adminNotes?: string) => {
    await showConfirm(
      {
        title: 'ยืนยันการอนุมัติ',
        message: 'คุณต้องการอนุมัติคำขอนี้ใช่หรือไม่?',
        confirmText: 'อนุมัติ',
        cancelText: 'ยกเลิก',
        variant: 'success'
      },
      async () => {
        setChangeActionLoading(true);
        try {
          await apiClient.put(`/api/admin/change-requests/${requestId}/approve`, {
            adminNotes: adminNotes || undefined
          });
          toast.success('อนุมัติคำขอสำเร็จ');
          setShowChangeRequestModal(false);

          // Reload data
          if (activeTab === 'CREATE') {
            await loadChangeRequests('CREATE', changeStatusFilter);
          } else if (activeTab === 'UPDATE') {
            await loadChangeRequests('UPDATE', changeStatusFilter);
          }
          await loadCounts();
        } catch (error: any) {
          console.error('Failed to approve request:', error);
          toast.error(error.response?.data?.message || 'ไม่สามารถอนุมัติคำขอได้');
        } finally {
          setChangeActionLoading(false);
        }
      }
    );
  };

  const handleRejectChangeRequest = async (requestId: string) => {
    const adminNotes = await prompt(
      'กรุณาระบุเหตุผลในการปฏิเสธ',
      'ปฏิเสธคำขอ',
      {
        placeholder: 'ระบุเหตุผล...',
        variant: 'warning',
        confirmText: 'ปฏิเสธ',
        cancelText: 'ยกเลิก'
      }
    );

    if (!adminNotes || !adminNotes.trim()) {
      if (adminNotes !== null) {
        toast.error('กรุณาระบุเหตุผลในการปฏิเสธ');
      }
      return;
    }

    setChangeActionLoading(true);
    try {
      await apiClient.put(`/api/admin/change-requests/${requestId}/reject`, {
        adminNotes: adminNotes.trim()
      });
      toast.success('ปฏิเสธคำขอสำเร็จ');
      setShowChangeRequestModal(false);

      // Reload data
      if (activeTab === 'CREATE') {
        await loadChangeRequests('CREATE', changeStatusFilter);
      } else if (activeTab === 'UPDATE') {
        await loadChangeRequests('UPDATE', changeStatusFilter);
      }
      await loadCounts();
    } catch (error: any) {
      console.error('Failed to reject request:', error);
      toast.error(error.response?.data?.message || 'ไม่สามารถปฏิเสธคำขอได้');
    } finally {
      setChangeActionLoading(false);
    }
  };

  // Helper: แปลงชื่อฟิลด์เป็นภาษาไทย
  const getFieldNameThai = (key: string): string => {
    const fieldNames: Record<string, string> = {
      nameTh: 'ชื่อบริษัท (ไทย)',
      nameEn: 'ชื่อบริษัท (English)',
      description: 'คำอธิบาย',
      phone1: 'เบอร์โทรศัพท์ 1',
      phone2: 'เบอร์โทรศัพท์ 2',
      email: 'อีเมล',
      lineId: 'Line ID',
      website: 'เว็บไซต์',
      address: 'ที่อยู่',
      province: 'จังหวัด',
      district: 'เขต/อำเภอ',
      postalCode: 'รหัสไปรษณีย์',
      companySize: 'ขนาดบริษัท',
      establishedYear: 'ปีที่ก่อตั้ง',
      licenseNumber: 'เลขใบอนุญาต',
      taxId: 'เลขประจำตัวผู้เสียภาษี',
      serviceTypes: 'ประเภทบริการ',
      serviceAreas: 'พื้นที่ให้บริการ',
    };
    return fieldNames[key] || key;
  };

  // Helper: แปลง service type UUIDs เป็นชื่อ
  const formatServiceTypes = (uuids: string[]): string => {
    if (!uuids || uuids.length === 0) return '-';

    const names = uuids.map(uuid => {
      const serviceType = serviceTypes.find(st => st.id === uuid);
      return serviceType ? serviceType.nameTh : uuid;
    });

    return names.join(', ');
  };

  // Helper: format ค่าให้อ่านง่าย
  const formatValue = (value: any, key?: string): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'ใช่' : 'ไม่';

    // Special handling for serviceTypes
    if (key === 'serviceTypes' && Array.isArray(value)) {
      return formatServiceTypes(value);
    }

    // Special handling for serviceAreas (เขต/จังหวัด)
    if (key === 'serviceAreas' && Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '-';
    }

    // Special handling for galleryImages
    if (key === 'galleryImages' && Array.isArray(value)) {
      if (value.length === 0) return 'ไม่มีรูปภาพ';
      return `${value.length} รูปภาพ: ${value.map((img: any) =>
        `${img.caption || 'ไม่มีคำอธิบาย'} (${img.displayOrder || 0})`
      ).join(', ')}`;
    }

    // Handle array of primitives
    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      // Check if array contains objects
      if (typeof value[0] === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return value.join(', ');
    }

    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Helper: ตรวจสอบว่าฟิลด์นี้เปลี่ยนหรือไม่
  const isFieldChanged = (key: string): boolean => {
    if (!selectedChangeRequest?.changedFields) return false;
    return key in selectedChangeRequest.changedFields;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FiClock className="mr-1" /> รอดำเนินการ
          </span>
        );
      case 'APPROVED':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FiCheckCircle className="mr-1" /> อนุมัติแล้ว
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <FiXCircle className="mr-1" /> ปฏิเสธ
          </span>
        );
      default:
        return null;
    }
  };

  const getRequestTypeBadge = (type: string) => {
    switch (type) {
      case 'UPDATE':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            แก้ไขข้อมูล
          </span>
        );
      case 'COMPANY_IMAGE_ADD':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            เพิ่มรูปภาพ
          </span>
        );
      case 'COMPANY_IMAGE_DELETE':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            ลบรูปภาพ
          </span>
        );
      case 'COMPANY_IMAGE_UPDATE':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            แก้ไขรูปภาพ
          </span>
        );
      default:
        return null;
    }
  };

  // Mobile Card Component for Change Requests
  const ChangeRequestMobileCard: React.FC<{
    request: ChangeRequest;
    onViewDetails: (request: ChangeRequest) => void;
  }> = ({ request, onViewDetails }) => {
    return (
      <div className="bg-white rounded-lg shadow p-4 border border-gray-200 hover:shadow-md transition-shadow">
        {/* Company Name */}
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">บริษัท</div>
          <div className="font-semibold text-gray-900">
            {request.requestedData?.nameTh || request.company?.nameTh || '-'}
          </div>
        </div>

        {/* User */}
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">ผู้ส่งคำขอ</div>
          <div className="text-sm font-medium text-gray-900">
            {request.user?.firstName} {request.user?.lastName}
          </div>
          <div className="text-xs text-gray-500">{request.user?.email || '-'}</div>
        </div>

        {/* Type and Status Badges */}
        <div className="mb-3 flex flex-wrap gap-2">
          {getRequestTypeBadge(request.type)}
          {getStatusBadge(request.status)}
        </div>

        {/* Date */}
        <div className="mb-4 text-sm text-gray-500">
          {new Date(request.createdAt).toLocaleString('th-TH')}
        </div>

        {/* Action Button - Full Width with Label */}
        <div className="pt-3 border-t border-gray-200">
          <button
            onClick={() => onViewDetails(request)}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <FiEye className="mr-2 h-4 w-4" />
            ดูรายละเอียด
          </button>
        </div>
      </div>
    );
  };

  // Filter claims based on search query
  const filteredClaims = claims.filter((claim) => {
    if (!claimsSearchQuery) return true;

    const searchLower = claimsSearchQuery.toLowerCase();
    const companyName = claim.company?.nameTh?.toLowerCase() || '';
    const userEmail = claim.user?.email?.toLowerCase() || '';
    const userName = `${claim.user?.firstName || ''} ${claim.user?.lastName || ''}`.toLowerCase();

    return (
      companyName.includes(searchLower) ||
      userEmail.includes(searchLower) ||
      userName.includes(searchLower)
    );
  });

  // Render content based on active tab
  const renderTabContent = () => {
    if (activeTab === 'CLAIM') {
      // === CLAIM TAB ===
      return (
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาด้วยชื่อบริษัท, อีเมลผู้ใช้, หรือชื่อผู้ใช้..."
              className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              value={claimsSearchQuery}
              onChange={(e) => setClaimsSearchQuery(e.target.value)}
            />
          </div>

          {/* Claims Grid */}
          <div className="space-y-4">
            {claimsLoading ? (
              <div className="flex items-center justify-center py-12">
                <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
                <span className="ml-2 text-gray-600">กำลังโหลด...</span>
              </div>
            ) : filteredClaims.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredClaims.map((claim) => (
                  <ClaimCard
                    key={claim.id}
                    claim={claim}
                    onApprove={handleApproveClaimClick}
                    onReject={handleRejectClaimClick}
                    onViewDetails={handleViewClaimDetails}
                    actionLoading={false}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่พบคำขอ</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {claimsSearchQuery ? 'ลองปรับเปลี่ยนคำค้นหา' : 'ไม่มีคำขอขอเป็นเจ้าของในขณะนี้'}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      // === CREATE / UPDATE TAB ===
      const currentType = activeTab;

      return (
        <div className="space-y-6">
          {/* Status Filter Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {['PENDING', 'APPROVED', 'REJECTED', 'ALL'].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setChangeStatusFilter(status);
                    loadChangeRequests(currentType, status);
                  }}
                  className={`${
                    changeStatusFilter === status
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  {status === 'PENDING' ? 'รอดำเนินการ' : status === 'APPROVED' ? 'อนุมัติแล้ว' : status === 'REJECTED' ? 'ปฏิเสธแล้ว' : 'ทั้งหมด'}
                </button>
              ))}
            </nav>
          </div>

          {/* Requests Display - Dual View Pattern */}
          {changeRequestsLoading ? (
            <Card className="p-12">
              <div className="flex items-center justify-center">
                <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
                <span className="ml-2 text-gray-600">กำลังโหลด...</span>
              </div>
            </Card>
          ) : changeRequests.length === 0 ? (
            <Card className="p-12">
              <div className="text-center text-gray-500">ไม่พบคำขอ</div>
            </Card>
          ) : (
            <>
              {/* Mobile Card View - แสดงเมื่อ < 768px */}
              <div className="md:hidden space-y-4">
                {changeRequests.map(request => (
                  <ChangeRequestMobileCard
                    key={request.id}
                    request={request}
                    onViewDetails={handleViewChangeRequestDetail}
                  />
                ))}
              </div>

              {/* Desktop Table View - แสดงเมื่อ >= 768px */}
              <Card className="hidden md:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">บริษัท</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ผู้ส่งคำขอ</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ประเภท</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่ส่ง</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">จัดการ</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {changeRequests.map(request => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {request.requestedData?.nameTh || request.company?.nameTh || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {request.user?.firstName} {request.user?.lastName}
                            </div>
                            <div className="text-xs text-gray-500">{request.user?.email || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getRequestTypeBadge(request.type)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(request.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(request.createdAt).toLocaleString('th-TH')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewChangeRequestDetail(request)}
                            >
                              <FiEye className="mr-1" /> ดูรายละเอียด
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      );
    }
  };

  return (
    <AdminLayout
      title="จัดการคำขอทั้งหมด"
      breadcrumbs={[
        { label: 'แดชบอร์ด', href: '/admin/dashboard' },
        { label: 'จัดการคำขอทั้งหมด' }
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <FiFileText className="mr-3 h-8 w-8 text-blue-600" />
              จัดการคำขอทั้งหมด
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              จัดการคำขอสร้างบริษัทใหม่, ขอเป็นเจ้าของ, และแก้ไขข้อมูล
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                loadCounts();
                if (activeTab === 'CLAIM') {
                  loadClaims(claimStatusFilter);
                } else if (activeTab === 'CREATE') {
                  loadChangeRequests('CREATE', changeStatusFilter);
                } else if (activeTab === 'UPDATE') {
                  loadChangeRequests('UPDATE', changeStatusFilter);
                }
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <FiRefreshCw className="mr-2 h-4 w-4" />
              รีเฟรช
            </button>
          </div>
        </div>

        {/* Main Tabs (Request Type) */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('CREATE')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'CREATE'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              สร้างบริษัทใหม่
              {createCount > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {createCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('CLAIM')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'CLAIM'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ขอเป็นเจ้าของ
              {claimCount > 0 && (
                <span className="ml-2 bg-amber-100 text-amber-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {claimCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('UPDATE')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'UPDATE'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              แก้ไขข้อมูล
              {updateCount > 0 && (
                <span className="ml-2 bg-purple-100 text-purple-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {updateCount}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Error Display */}
        {errorState.hasError && errorState.error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FiAlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">เกิดข้อผิดพลาด</h3>
                <p className="mt-1 text-sm text-red-700">{errorState.error.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {renderTabContent()}
      </div>

      {/* === CLAIM Modals === */}
      <ApproveClaimModal
        claim={selectedClaim}
        isOpen={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedClaim(null);
        }}
        onConfirm={handleApproveClaimConfirm}
        loading={claimModalLoading}
      />

      <RejectClaimModal
        claim={selectedClaim}
        isOpen={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedClaim(null);
        }}
        onConfirm={handleRejectClaimConfirm}
        loading={claimModalLoading}
      />

      <ViewClaimDetailsModal
        claim={selectedClaim}
        isOpen={showClaimDetailsModal}
        onClose={() => {
          setShowClaimDetailsModal(false);
          setSelectedClaim(null);
        }}
      />

      {/* === CHANGE REQUEST Modal === */}
      {showChangeRequestModal && selectedChangeRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setShowChangeRequestModal(false)}
          />

          {/* Modal */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full max-w-3xl"
            >
              <Card className="p-6 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      รายละเอียดคำขอ
                      {getRequestTypeBadge(selectedChangeRequest.type)}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedChangeRequest.type === 'CREATE' && 'เพิ่มบริษัทใหม่'}
                      {selectedChangeRequest.type === 'UPDATE' && 'แก้ไขข้อมูลบริษัท'}
                      {selectedChangeRequest.type === 'COMPANY_IMAGE_ADD' && 'เพิ่มรูปภาพไปยัง Gallery'}
                      {selectedChangeRequest.type === 'COMPANY_IMAGE_DELETE' && 'ลบรูปภาพจาก Gallery'}
                      {selectedChangeRequest.type === 'COMPANY_IMAGE_UPDATE' && 'แก้ไขรูปภาพใน Gallery'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowChangeRequestModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>

                {/* Request Info */}
                <div className="mb-6 space-y-3">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-500">สถานะ:</span>
                    {getStatusBadge(selectedChangeRequest.status)}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">ผู้ส่งคำขอ:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {selectedChangeRequest.user?.email}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">วันที่ส่งคำขอ:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {new Date(selectedChangeRequest.createdAt).toLocaleString('th-TH')}
                    </span>
                  </div>
                </div>

                {/* Before/After Comparison for UPDATE */}
                {selectedChangeRequest.type === 'UPDATE' && selectedChangeRequest.currentData && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">เปรียบเทียบการเปลี่ยนแปลง</h3>

                    {/* Summary Badge */}
                    {selectedChangeRequest.changedFields && Object.keys(selectedChangeRequest.changedFields).length > 0 && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm font-medium text-yellow-900">
                          เปลี่ยนแปลง {Object.keys(selectedChangeRequest.changedFields).length} ฟิลด์: {' '}
                          <span className="font-normal">
                            {Object.keys(selectedChangeRequest.changedFields).map(k => getFieldNameThai(k)).join(', ')}
                          </span>
                        </p>
                      </div>
                    )}

                    {/* Comparison Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* BEFORE - Current Data */}
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                          <FiClock className="mr-2 h-4 w-4" />
                          ข้อมูลปัจจุบัน (Before)
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(selectedChangeRequest.currentData)
                            .filter(([key]) => key in (selectedChangeRequest.requestedData || {}))
                            .map(([key, value]) => (
                              <div
                                key={key}
                                className={`p-2 rounded ${
                                  isFieldChanged(key)
                                    ? 'bg-red-50 border border-red-200'
                                    : 'bg-white border border-gray-100'
                                }`}
                              >
                                <p className="text-xs font-medium text-gray-500">{getFieldNameThai(key)}</p>
                                <p className={`text-sm mt-1 ${
                                  isFieldChanged(key) ? 'text-red-700 line-through' : 'text-gray-900'
                                }`}>
                                  {formatValue(value, key)}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>

                      {/* AFTER - Requested Data */}
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                          <FiCheckCircle className="mr-2 h-4 w-4" />
                          ข้อมูลที่ขอเปลี่ยน (After)
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(selectedChangeRequest.requestedData).map(([key, value]) => (
                            <div
                              key={key}
                              className={`p-2 rounded ${
                                isFieldChanged(key)
                                  ? 'bg-green-50 border border-green-300'
                                  : 'bg-white border border-gray-100'
                              }`}
                            >
                              <p className="text-xs font-medium text-gray-500">{getFieldNameThai(key)}</p>
                              <p className={`text-sm mt-1 font-medium ${
                                isFieldChanged(key) ? 'text-green-700' : 'text-gray-900'
                              }`}>
                                {formatValue(value, key)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Requested Data for CREATE */}
                {selectedChangeRequest.type === 'CREATE' && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">ข้อมูลบริษัทใหม่</h3>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="space-y-2">
                        {Object.entries(selectedChangeRequest.requestedData).map(([key, value]) => (
                          <div key={key} className="p-2 bg-white rounded border border-gray-100">
                            <p className="text-xs font-medium text-gray-500">{getFieldNameThai(key)}</p>
                            <p className="text-sm mt-1 text-gray-900">{formatValue(value, key)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* User Notes */}
                {selectedChangeRequest.userNotes && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">หมายเหตุจากผู้ใช้</h3>
                    <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded">
                      {selectedChangeRequest.userNotes}
                    </p>
                  </div>
                )}

                {/* Admin Notes */}
                {selectedChangeRequest.adminNotes && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">หมายเหตุจาก Admin</h3>
                    <p className="text-sm text-gray-700 bg-yellow-50 p-3 rounded">
                      {selectedChangeRequest.adminNotes}
                    </p>
                  </div>
                )}

                {/* Actions */}
                {selectedChangeRequest.status === 'PENDING' && (
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <Button
                      variant="destructive"
                      onClick={() => handleRejectChangeRequest(selectedChangeRequest.id)}
                      disabled={changeActionLoading}
                      leftIcon={<FiX />}
                    >
                      ปฏิเสธ
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => handleApproveChangeRequest(selectedChangeRequest.id)}
                      disabled={changeActionLoading}
                      leftIcon={<FiCheck />}
                      loading={changeActionLoading}
                      loadingText="กำลังดำเนินการ..."
                    >
                      อนุมัติ
                    </Button>
                  </div>
                )}
              </Card>
            </motion.div>
          </div>
        </div>
      )}

      {/* Dialog Components */}
      {ConfirmDialog}
      {AlertDialog}
    </AdminLayout>
  );
};

export default UnifiedRequestsPage;
